"use client";

import * as React from "react";
import { cn } from "../lib";

/** Stop-preview pulse cycle — ~50% slower than notification-dot PulseRing (2.4s). */
export const HEXAGON_PULSE_RING_DURATION_S = 4.8;

/** Stagger between rings — scaled with {@link HEXAGON_PULSE_RING_DURATION_S}. */
export const HEXAGON_PULSE_RING_STAGGER_S = 1;

/**
 * Same clip as path/grid tile paint layers ({@link HexagonPathTile} /
 * {@link HexagonGridTile}). Use **inner** clip — outer (`--hex-tile-outer-clip`)
 * falls back to circles in Safari (LEARNINGS: Grid tile clipping).
 */
export const HEXAGON_PULSE_RING_CLIP_CLASS =
	"origin-center [clip-path:var(--hex-tile-inner-clip)]";

export interface HexagonPulseRingProps {
	className?: string;
	/**
	 * Seconds to offset the animation start (useful for staggering multiple rings).
	 */
	delay?: number;
	/**
	 * Animation duration in seconds. Default is the slower stop-preview cadence.
	 */
	durationS?: number;
	/**
	 * Fill for each pulsing hex. Defaults to theme-aware translucent plates.
	 */
	fillClassName?: string;
}

/**
 * Concentric pulsing filled hexes — same `--hex-tile-inner-clip` as path tiles,
 * plus PulseRing scale/fade/stagger. Must sit under a map stage that sets the var.
 */
export const HexagonPulseRing = React.memo(function HexagonPulseRing({
	className,
	delay = 0,
	durationS = HEXAGON_PULSE_RING_DURATION_S,
	fillClassName,
}: HexagonPulseRingProps) {
	const css = React.useMemo(
		() => `
@keyframes sq-hexagon-pulse-ring-scale {
	from { transform: scale(0.5); opacity: 0.55; }
	to { transform: scale(2.5); opacity: 0; }
}
.sq-hexagon-pulse-ring-hex {
	position: absolute;
	inset: 0;
	transform: scale(0.5);
	opacity: 0.55;
	pointer-events: none;
	animation: sq-hexagon-pulse-ring-scale ${durationS}s infinite cubic-bezier(0.4, 0, 0.6, 1);
	animation-fill-mode: backwards;
}
@media (prefers-reduced-motion: reduce) {
	.sq-hexagon-pulse-ring-hex {
		animation: none;
		opacity: 0;
	}
}
`,
		[durationS],
	);

	const fill = cn("bg-black/25 dark:bg-white/35", fillClassName);
	const staggerDelays = [
		delay,
		delay + HEXAGON_PULSE_RING_STAGGER_S,
		delay + HEXAGON_PULSE_RING_STAGGER_S * 2,
		delay + HEXAGON_PULSE_RING_STAGGER_S * 3,
	];

	return (
		<>
			<style>{css}</style>
			<div
				className={cn(
					"pointer-events-none absolute inset-0 overflow-visible",
					className,
				)}
				aria-hidden
				data-testid="hexagon-pulse-ring"
			>
				{staggerDelays.map((animationDelay) => (
					<div
						key={animationDelay}
						className={cn(
							"sq-hexagon-pulse-ring-hex",
							HEXAGON_PULSE_RING_CLIP_CLASS,
							fill,
						)}
						style={{ animationDelay: `${animationDelay}s` }}
					/>
				))}
			</div>
		</>
	);
});
